import React, { FC } from "react";
import { Form, Upload } from "antd";
import { ImportOutlined } from "@ant-design/icons";
import { DocumentNode } from "@apollo/client";
import { ApplicationError } from "@design-system";
import { CREATE_FILE_DATA_IMPORT } from "../../graphql/mutation/data-import";
import { translateError, translateGraphQLErrors } from "../../error";
import {
  createFileDataImport,
  createFileDataImport_createFileDataImport_dataFrameResults,
  createFileDataImport_createFileDataImport_dataFrameResults_details,
  createFileDataImportVariables,
} from "../../graphql/__generated_types__/createFileDataImport";
import { UploadFile } from "antd/lib/upload/interface";
import { useMutation } from "../../hooks";

const { Dragger } = Upload;

export enum FileFormat {
  BMP = "bmp",
  GIF = "gif",
  JPG = "jpg",
  JPEG = "jpeg",
  PNG = "png",
  SVG = "svg",
  WEBP = "webp",
  JSON = "json",
  XLSX = "xlsx",
  CSV = "csv",
}

export type DataImportDetail = createFileDataImport_createFileDataImport_dataFrameResults_details;
export type DataFrameImportResult = createFileDataImport_createFileDataImport_dataFrameResults;
export type DataImportResult = DataFrameImportResult[] | ApplicationError;
export type DataImportFormLabels = {
  importHintText?: string;
};
export type DataImportFormProps = {
  importType: any;
  fetchQuery?: DocumentNode;
  onImportStarted: () => void;
  onImportCompleted: (result: DataImportResult) => void;
  labels: DataImportFormLabels;
};

export const DataImportForm: FC<DataImportFormProps> = ({
  importType,
  fetchQuery,
  onImportStarted,
  onImportCompleted,
  labels: { importHintText },
}) => {
  const [mutationFunction] = useMutation<createFileDataImport, createFileDataImportVariables>(
    CREATE_FILE_DATA_IMPORT,
    {}
  );
  const onChangeUpload = async (file: File) => {
    try {
      onImportStarted();
      const { data, errors } = await mutationFunction({
        variables: {
          upload: file,
          type: importType,
        },
        context: {
          hasUpload: true,
        },
        refetchQueries: [{ query: fetchQuery! }],
        awaitRefetchQueries: true,
      });
      onImportCompleted(
        !errors
          ? data!.createFileDataImport.dataFrameResults
          : translateGraphQLErrors(errors, "Failed to import data")
      );
    } catch (err) {
      onImportCompleted(translateError(err as any, "Failed to import data"));
    }
  };

  return (
    <Form>
      <Form.Item
        name="dragger"
        valuePropName="fileList"
        noStyle
        getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
      >
        <Dragger
          accept="csv"
          name="files"
          openFileDialogOnClick={true}
          onChange={async (uc) => {
            const firstFile =
              (uc.fileList[0] as UploadFile) ||
              ({ percent: 0, status: "removed", originFileObj: {} } as UploadFile);
            const { percent: firstFilePercent, status: firstFileStatus, originFileObj } = firstFile;

            if (0 === firstFilePercent && "uploading" === firstFileStatus) {
              await onChangeUpload(originFileObj as File);
            }
          }}
          showUploadList={false}
          multiple={false}
          maxCount={1}
        >
          <p className="ant-upload-drag-icon">
            <ImportOutlined />
          </p>
          <p className="ant-upload-text">Drag data import file to this area or click to select</p>
          <p className="ant-upload-hint">{importHintText}</p>
        </Dragger>
      </Form.Item>
    </Form>
  );
};
